/**
 * Генератор волн: состав, время и точки появления — из сида и данных `balance.waves` (E1).
 *
 * Обычные зомби выходят равномерно (с дрожанием) от конца отсчёта до `targetTicks[0]`, поэтому волна длится не
 * меньше минимума; верхнюю границу держит баланс (урон против прочности, E2/E6). Каждая `bossEvery`-я волна —
 * с боссом, боссы по кругу `bossOrder`; босс выходит в середине окна появления.
 */
import type { BalanceData, FieldGeometry, RunState, ZombieDef, ZombieKind } from "@newfungame/contracts";
import { PERMILLE } from "@newfungame/contracts";
import { perimeter, perimeterPoint } from "./layout";
import { divTrunc, mulPermille } from "./math";
import type { Rng } from "./rng";

/** Одно появление в плане волны. */
export interface SpawnEntry {
  /** Тик волны, на котором зомби появляется. */
  tick: number;
  kind: ZombieKind | "boss";
  bossId: string | null;
  x: number;
  y: number;
}

/** План волны: всё, что выбрал генератор. */
export interface WavePlan {
  wave: number;
  isBoss: boolean;
  bossId: string | null;
  /** Длина отсчёта перед боем (фаза `intro`), тиков. */
  introTicks: number;
  /** Рост прочности зомби и щитов на этой волне, промилле (1000 — как в данных). */
  hpPermille: number;
  /** Появления по возрастанию тика. */
  spawns: SpawnEntry[];
}

export function isBossWave(balance: BalanceData, wave: number): boolean {
  const every = balance.waves.bossEvery;
  return every > 0 && wave % every === 0;
}

/** Босс волны (по кругу `bossOrder`) или `null`. */
export function bossForWave(balance: BalanceData, wave: number): string | null {
  if (!isBossWave(balance, wave)) return null;
  const order = balance.waves.bossOrder;
  return order[(wave / balance.waves.bossEvery - 1) % order.length] ?? null;
}

/**
 * Бета: где волна в уровне. `null` — обычный режим (`waves.levels` нет). Волна движка = подволна уровня
 * (`waves.levels.subwaves` на уровень), локация — по кругу `locations`, каждая `locationEveryLevels` уровней.
 */
export function levelOf(
  balance: BalanceData,
  wave: number,
): { level: number; subwave: number; subwaves: number; location: string; isBossLevel: boolean } | null {
  const l = balance.waves.levels;
  if (!l) return null;
  const w = Math.max(1, wave);
  const level = Math.floor((w - 1) / l.subwaves) + 1;
  return {
    level,
    subwave: ((w - 1) % l.subwaves) + 1,
    subwaves: l.subwaves,
    location: l.locations[Math.floor((level - 1) / l.locationEveryLevels) % l.locations.length]!,
    isBossLevel: level % l.bossEveryLevels === 0,
  };
}

/** Волна, после которой игрок выбирает усиление «1 из 3» и получает награду: всегда в обычном режиме, в бете — последняя подволна. */
export function isOfferWave(balance: BalanceData, wave: number): boolean {
  const l = levelOf(balance, wave);
  return l === null || l.subwave === l.subwaves;
}

/** Первая волна уровня, в котором волна `wave` (бета); в обычном режиме — `wave`. */
export function levelStartWave(balance: BalanceData, wave: number): number {
  const l = balance.waves.levels;
  return l ? Math.floor((Math.max(1, wave) - 1) / l.subwaves) * l.subwaves + 1 : wave;
}

/** Рост прочности на волне, промилле. */
export function hpPermilleForWave(balance: BalanceData, wave: number): number {
  const { hpGrowthPermille, hpSoft, hpEarlyPermille } = balance.waves;
  const early = hpEarlyPermille?.[wave - 1];
  if (early !== undefined) return early;
  if (!hpSoft || wave <= hpSoft.fromWave) return PERMILLE + hpGrowthPermille * (wave - 1);
  // до fromWave — как раньше; дальше приращение за волну мягче (поздняя игра не упирается в стену роста врагов)
  return PERMILLE + hpGrowthPermille * (hpSoft.fromWave - 1) + hpSoft.growthPermille * (wave - hpSoft.fromWave);
}

/** Стаков усилений в забежке. */
export function runStacks(run: Pick<RunState, "boosts">): number {
  return run.boosts.reduce((s, b) => s + b.stacks, 0);
}

/**
 * Рост прочности волны с усталостью забежки: стаки сверх `runFatigue.freeStacks` добавляют прочность. Без
 * `runFatigue` — {@link hpPermilleForWave}.
 */
export function runHpPermille(balance: BalanceData, run: Pick<RunState, "wave" | "boosts">): number {
  const base = hpPermilleForWave(balance, run.wave);
  const f = balance.waves.runFatigue;
  if (!f) return base;
  const n = Math.max(0, runStacks(run) - f.freeStacks);
  if (!f.compound) return mulPermille(base, PERMILLE + n * f.permillePerStack);
  let hp = base;
  for (let i = 0; i < n; i++) hp = mulPermille(hp, PERMILLE + f.permillePerStack);
  return hp;
}

/** Прочность с ростом по волне (не меньше 1). */
export function scaledHp(hp: number, hpPermille: number): number {
  return Math.max(1, mulPermille(hp, hpPermille));
}

/** Число обычных зомби в волне (без босса и призванных). */
export function zombieCount(balance: BalanceData, wave: number): number {
  const { base, perWave, max } = balance.waves.count;
  const n = base + perWave * (wave - 1);
  return max === undefined ? n : Math.min(n, max);
}

/**
 * На какую волну откатывается забежка после проигрыша волны `wave`. `bestWave` — лучшая пройденная волна игрока: босс
 * ниже неё чекпойнт, откат идёт не дальше волны после него. Выше проигранной волны откат не поднимает.
 */
export function rollbackWave(balance: BalanceData, wave: number, bestWave = 0): number {
  const { defeatRollback, bossCheckpoint, bossEvery } = balance.waves;
  // бета: проигрыш откатывает на начало уровня
  if (balance.waves.levels) return levelStartWave(balance, wave);
  const to = Math.max(1, wave - defeatRollback);
  if (!bossCheckpoint || bossEvery <= 0) return to;
  const afterBoss = Math.floor(bestWave / bossEvery) * bossEvery + 1;
  return Math.min(wave, Math.max(to, afterBoss));
}

export function pickKind(pool: readonly ZombieDef[], rng: Rng): ZombieKind {
  let total = 0;
  for (const z of pool) total += z.weight;
  let r = rng.int(0, total - 1);
  for (const z of pool) {
    if (r < z.weight) return z.kind;
    r -= z.weight;
  }
  return pool[pool.length - 1]!.kind;
}

/** Случайная точка на краю поля. */
export function spawnPoint(field: FieldGeometry, rng: Rng): { x: number; y: number } {
  return perimeterPoint(field, rng.int(0, perimeter(field) - 1));
}

/**
 * Точка появления зомби волны 1: верхний или нижний край по очереди, без боковых полос, которые телефон обрезает
 * (`field/iso.ts`, `crop`). Так пачка видна с первого кадра, а не входит в кадр через секунды.
 */
function openingPoint(field: FieldGeometry, index: number, rng: Rng): { x: number; y: number } {
  const half = divTrunc(field.width * 3, 10);
  return { x: rng.int(-half, half), y: index % 2 === 0 ? field.depth : 0 };
}

/** Построить план волны. Порядок обращений к ГПСЧ фиксирован — часть детерминизма журнала. */
export function generateWave(balance: BalanceData, wave: number, rng: Rng, field: FieldGeometry): WavePlan {
  const opening = wave === 1 ? balance.waves.opening : undefined;
  const bossId = bossForWave(balance, wave);
  const lv = levelOf(balance, wave);
  const pause =
    bossId === null
      ? lv && lv.subwave > 1
        ? balance.waves.levels!.subwaveBreakTicks
        : balance.waves.breakTicks
      : (balance.waves.bossBreakTicks ?? balance.waves.breakTicks);
  const intro = opening?.introTicks ?? pause;
  const last = Math.max(intro, balance.waves.targetTicks[0]);
  const window = last - intro;
  const n = opening?.count ?? zombieCount(balance, wave);

  let pool = balance.zombies.filter((z) => z.minWave <= wave);
  if (pool.length === 0) {
    const min = Math.min(...balance.zombies.map((z) => z.minWave));
    pool = balance.zombies.filter((z) => z.minWave === min);
  }

  const spawns: SpawnEntry[] = [];
  const step = n > 1 ? divTrunc(window, n - 1) : 0;
  for (let i = 0; opening && i < n; i++) {
    const tick = intro + divTrunc(i, opening.packSize) * opening.packGapTicks;
    const kind = pickKind(pool, rng);
    const p = openingPoint(field, i, rng);
    spawns.push({ tick, kind, bossId: null, x: p.x, y: p.y });
  }
  for (let i = 0; !opening && i < n; i++) {
    let tick = n > 1 ? intro + divTrunc(i * window, n - 1) : intro;
    if (i > 0 && i < n - 1 && step > 1) tick += rng.int(0, step) - divTrunc(step, 2);
    const kind = pickKind(pool, rng);
    const p = spawnPoint(field, rng);
    spawns.push({ tick: Math.min(last, Math.max(intro, tick)), kind, bossId: null, x: p.x, y: p.y });
  }

  if (bossId) {
    const p = spawnPoint(field, rng);
    spawns.push({ tick: intro + divTrunc(window, 2), kind: "boss", bossId, x: p.x, y: p.y });
  }

  // стабильная сортировка по тику (Array.prototype.sort стабилен с ES2019)
  spawns.sort((a, b) => a.tick - b.tick);
  return {
    wave,
    isBoss: bossId !== null,
    bossId,
    introTicks: intro,
    hpPermille: hpPermilleForWave(balance, wave),
    spawns,
  };
}
